import {
  ReactElement,
  ReactNode,
  isValidElement,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { IButtonProps } from '@/common/components/Button/Button';
import { ModalHeader } from '@/common/components/Modal/Modal';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { OverlayBottom } from '@/common/components/Overlay/OverlayBottom/OverlayBottom';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import * as DropdownMenuPrimitive from '@rn-primitives/dropdown-menu';
import { styles } from './ContextMenu.styles';

export type TContextMenuProps<T> = {
  title?: string;
  trigger: ReactElement<IButtonProps>;
  options: {
    item?: T;
    icon?: ReactNode;
    label: string;
    action: () => void;
  }[];
  renderItem?: (item?: T) => ReactNode;
  menuClassName?: string;
};

export function ContextMenu<T>(props: TContextMenuProps<T>) {
  const { sm } = useBreakpoints(['sm']);

  if (sm) {
    return <ContextMenuCenter {...props} />;
  }

  return <ContextMenuBottom {...props} />;
}

export function ContextMenuCenter<T>({
  trigger,
  options,
  renderItem,
  menuClassName,
}: TContextMenuProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenuPrimitive.Root onOpenChange={setIsOpen}>
      <DropdownMenuPrimitive.Trigger className={styles.trigger} asChild>
        {trigger}
      </DropdownMenuPrimitive.Trigger>
      {isOpen && (
        <ContextMenuItems
          options={options}
          renderItem={renderItem}
          className={menuClassName}
        />
      )}
    </DropdownMenuPrimitive.Root>
  );
}

function ContextMenuBottom<T>({
  title,
  trigger,
  options,
  renderItem,
}: TContextMenuProps<T>) {
  const bottomOverlayRef = useRef<TOverlayRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  const triggerElement = isValidElement(trigger) ? (
    <trigger.type {...trigger.props} onPress={() => setIsOpen(true)} />
  ) : (
    <Pressable onPress={() => setIsOpen(true)}>{trigger}</Pressable>
  );

  return (
    <>
      {triggerElement}
      {isOpen && (
        <OverlayBottom
          header={
            <>
              <View className={styles.handle} />
              <ModalHeader title={title} closeOnX />
            </>
          }
          onClose={() => setIsOpen(false)}
          ref={bottomOverlayRef}
          hostName="root"
        >
          <View className={styles.optionsContainer}>
            {options.map((option, index) => (
              <Pressable
                key={index}
                className={styles.option}
                onPress={option.action}
              >
                {renderItem ? (
                  renderItem(option.item)
                ) : (
                  <>
                    {option.icon}
                    <BodyText bold>{option.label}</BodyText>
                  </>
                )}
              </Pressable>
            ))}
          </View>
        </OverlayBottom>
      )}
    </>
  );
}

type TContextMenuItemsProps<T> = Pick<
  TContextMenuProps<T>,
  'options' | 'renderItem'
> & {
  className?: string;
};

function ContextMenuItems<T>({
  options,
  renderItem,
  className,
}: TContextMenuItemsProps<T>) {
  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  };
  const { triggerPosition } = DropdownMenuPrimitive.useRootContext();
  const contentWidth =
    Platform.OS !== 'web' ? triggerPosition?.width : undefined;

  const container = useRef(
    Platform.OS === 'web'
      ? document.getElementById('primitives-root')
      : undefined
  );
  return (
    <DropdownMenuPrimitive.Portal container={container.current} hostName="root">
      <DropdownMenuPrimitive.Overlay
        style={StyleSheet.absoluteFill}
        className="z-[1]"
      >
        <DropdownMenuPrimitive.Content
          sideOffset={8}
          insets={contentInsets}
          className={styles.content({ className })}
          style={{
            width: contentWidth,
          }}
        >
          <ScrollView className={styles.group}>
            {options.map((option, index) => {
              return (
                <DropdownMenuPrimitive.Item
                  key={index}
                  className={styles.item}
                  onPress={option.action}
                >
                  <Stack horizontal align="center" gap="sm">
                    {renderItem ? (
                      renderItem(option.item)
                    ) : (
                      <>
                        {option.icon}
                        <BodyText bold>{option.label}</BodyText>
                      </>
                    )}
                  </Stack>
                </DropdownMenuPrimitive.Item>
              );
            })}
          </ScrollView>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Overlay>
    </DropdownMenuPrimitive.Portal>
  );
}
